import { useTranslation } from 'react-i18next'
import { Fragment, useCallback, useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type ColumnFiltersState,
  type VisibilityState,
  type RowSelectionState,
  type Updater,
  type ExpandedState,
  type Row,
} from '@tanstack/react-table'
import { cn } from '@/shared/lib/utils'
import { DataTableColumnHeader } from './data-table-column-header'
import { DataTablePagination } from './data-table-pagination'
import { DataTableToolbar } from './data-table-toolbar'

export interface DataTableProps<TData> {
  columns: ColumnDef<TData>[]
  data: TData[]
  totalRows: number
  /** When provided, switches pagination to "unknown total" mode (no page count shown). */
  hasMore?: boolean
  isLoading?: boolean
  /** Replaces the «No results.» line when there are no rows (an empty state with its own words and action). */
  emptyState?: React.ReactNode
  // Server-side controlled state
  sorting: SortingState
  onSortingChange: (sorting: SortingState) => void
  pagination: PaginationState
  onPaginationChange: (pagination: PaginationState) => void
  columnFilters: ColumnFiltersState
  onColumnFiltersChange: (filters: ColumnFiltersState) => void
  // Optional controlled state
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (visibility: VisibilityState) => void
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (selection: RowSelectionState) => void
  onExport?: () => void
  /** Callback when a row is clicked — useful for detail panel selection */
  onRowClick?: (row: TData) => void
  /** Key of the currently selected/active row for highlight styling */
  activeRowId?: string
  /** Default visibility applied when no state is provided */
  defaultColumnVisibility?: VisibilityState
  /**
   * Legacy localStorage key for uncontrolled column visibility persistence
   * (key `dt-cols-<storageKey>`). Ignored in controlled mode.
   */
  storageKey?: string
  /** When true, renders the "Reset view" button in the toolbar */
  hasSavedView?: boolean
  /** Called when the user clicks "Reset view" */
  onResetView?: () => void
  className?: string
  /** When set, rows can expand and this renders the expanded content row. */
  renderSubRow?: (row: Row<TData>) => React.ReactNode
  /** Whether a given row is expandable. Defaults to all rows when renderSubRow is set. */
  getRowCanExpand?: (row: Row<TData>) => boolean
  /** When true, clicking anywhere on a row toggles its expansion (opt-in; other tables use an expander). */
  expandOnRowClick?: boolean
  /** Classes for a row (e.g. a status colour). When it returns classes they replace that row's zebra stripe and hover. */
  getRowClassName?: (row: TData) => string | undefined
}

function resolve<T>(updater: Updater<T>, current: T): T {
  return typeof updater === 'function' ? (updater as (v: T) => T)(current) : updater
}

export function DataTable<TData>({
  columns,
  data,
  totalRows,
  hasMore,
  isLoading = false,
  emptyState,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  columnFilters,
  onColumnFiltersChange,
  columnVisibility: controlledVisibility,
  onColumnVisibilityChange,
  rowSelection,
  onRowSelectionChange,
  onExport,
  onRowClick,
  activeRowId,
  defaultColumnVisibility,
  storageKey,
  hasSavedView,
  onResetView,
  className,
  renderSubRow,
  getRowCanExpand,
  expandOnRowClick,
  getRowClassName,
}: DataTableProps<TData>) {
  const { t } = useTranslation()
  const computedDefaults = useMemo<VisibilityState>(() => {
    const fromMeta: VisibilityState = {}
    for (const col of columns) {
      if (col.meta?.defaultHidden) {
        const key = ('id' in col && col.id) || ('accessorKey' in col && col.accessorKey)
        if (typeof key === 'string') fromMeta[key] = false
      }
    }
    return { ...fromMeta, ...(defaultColumnVisibility ?? {}) }
  }, [columns, defaultColumnVisibility])

  const [localVisibility, setLocalVisibility] = useState<VisibilityState>(() => {
    if (controlledVisibility || !storageKey) return computedDefaults
    try {
      const stored = localStorage.getItem(`dt-cols-${storageKey}`)
      return stored
        ? { ...computedDefaults, ...(JSON.parse(stored) as VisibilityState) }
        : computedDefaults
    } catch {
      return computedDefaults
    }
  })

  // In controlled mode, merge computed defaults under the controlled state so
  // callers don't have to re-inject meta.defaultHidden or defaultColumnVisibility.
  const visibility = useMemo<VisibilityState>(
    () =>
      controlledVisibility
        ? { ...computedDefaults, ...controlledVisibility }
        : localVisibility,
    [controlledVisibility, computedDefaults, localVisibility],
  )

  const handleVisibilityChange = useCallback(
    (updater: Updater<VisibilityState>) => {
      const next = resolve(updater, visibility)
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(next)
      } else {
        setLocalVisibility(next)
        if (storageKey) {
          try {
            localStorage.setItem(`dt-cols-${storageKey}`, JSON.stringify(next))
          } catch {
            /* ignore quota errors */
          }
        }
      }
    },
    [visibility, onColumnVisibilityChange, storageKey],
  )

  const [expanded, setExpanded] = useState<ExpandedState>({})

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility: visibility,
      rowSelection: rowSelection ?? {},
      expanded,
    },
    onSortingChange: (u) => onSortingChange(resolve(u, sorting)),
    onPaginationChange: (u) => onPaginationChange(resolve(u, pagination)),
    onColumnFiltersChange: (u) => onColumnFiltersChange(resolve(u, columnFilters)),
    onColumnVisibilityChange: handleVisibilityChange,
    onRowSelectionChange: onRowSelectionChange
      ? (u) => onRowSelectionChange(resolve(u, rowSelection ?? {}))
      : undefined,
    onExpandedChange: setExpanded,
    getRowCanExpand: renderSubRow ? (getRowCanExpand ?? (() => true)) : undefined,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalRows,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableMultiSort: true,
    columnResizeMode: 'onChange',
    // Backend owns all filtering (manualFiltering above). A no-op filterFn
    // prevents TanStack's default autoRemove (e.g. inNumberRange rejecting
    // our {filterType,type,filter} objects) from silently dropping column
    // filters before they reach the query.
    defaultColumn: {
      filterFn: () => true,
    },
  })

  const rows = table.getRowModel().rows

  return (
    <div className={cn('flex flex-col gap-2 h-full min-h-0', className)}>
      <DataTableToolbar
        table={table}
        onExport={onExport}
        hasSavedView={hasSavedView}
        onResetView={onResetView}
      />

      <div className="flex-1 overflow-hidden rounded-md border bg-card min-h-0">
        <div className="h-full overflow-auto">
          <table className="w-full text-sm border-collapse" style={{ tableLayout: 'fixed' }}>
            <thead className="sticky top-0 z-10 bg-card">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b">
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="relative h-9 px-3 text-left font-normal overflow-hidden"
                      style={{ width: header.getSize() }}
                    >
                      {!header.isPlaceholder && (
                        <DataTableColumnHeader
                          column={header.column}
                          title={
                            typeof header.column.columnDef.header === 'string'
                              ? header.column.columnDef.header
                              : (header.column.columnDef.meta as { label?: string } | undefined)
                                  ?.label ?? header.column.id
                          }
                        />
                      )}
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none hover:bg-primary/30 active:bg-primary"
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={columns.length} className="h-32 text-center text-sm text-muted-foreground">
                    {t('dataTable.loading')}
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="h-32 text-center text-sm text-muted-foreground">
                    {emptyState ?? t('dataTable.noResults')}
                  </td>
                </tr>
              )}
              {!isLoading &&
                rows.map((row) => (
                  <Fragment key={row.id}>
                    <tr
                      className={cn(
                        'border-b transition-colors',
                        getRowClassName?.(row.original) ?? 'odd:bg-muted/30 hover:bg-accent/40',
                        row.getIsSelected() && 'bg-muted/60',
                        activeRowId && (row.original as Record<string, unknown>).id === activeRowId && 'bg-primary/10',
                        row.getIsExpanded() && 'bg-primary/5',
                        (onRowClick || (expandOnRowClick && renderSubRow)) && 'cursor-pointer',
                      )}
                      onClick={() => {
                        if (expandOnRowClick && renderSubRow && row.getCanExpand()) row.toggleExpanded()
                        onRowClick?.(row.original)
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            // overflow-hidden is not cosmetic: the table is laid out fixed, so a long
                            // unbreakable value (a file name, a derived th:<sha> reference) paints
                            // straight over the next column unless the cell clips it.
                            'h-9 px-3 py-1 align-middle overflow-hidden',
                            cell.column.columnDef.meta?.align === 'right' && 'text-right tabular-nums',
                            cell.column.columnDef.meta?.align === 'center' && 'text-center',
                          )}
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {renderSubRow && row.getIsExpanded() && (
                      <tr className="border-b bg-muted/20">
                        <td colSpan={row.getVisibleCells().length} className="px-3 py-4">
                          {renderSubRow(row)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <DataTablePagination table={table} totalRows={totalRows} hasMore={hasMore} />
    </div>
  )
}
